import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import styles from './ActiveChat.module.css';
import PlayablesCatalog from '../playables-catalog';
import PlayablesCatalogMobileDrawer from '../playables-catalog/PlayablesCatalogMobileDrawer';
import ActiveChatPersonaBar from './ActiveChatPersonaBar';
import ActiveChatTopBar from './ActiveChatTopBar';
import ActiveChatMessages from './ActiveChatMessages';
import ActiveChatInputBar from './ActiveChatInputBar';
import TeaserMessage from './TeaserMessage';
import { api } from '../../api';
import { getPlayableById } from '../playables-catalog';
import LoginModal from '../shared/LoginModal';
import { PlayableResult, InputableResult } from '../playables-catalog/catalog';
import { ChatType } from '../../api/chats';
import { MessageType, ChatMember, PreviewCharacter } from '../../types/message';
import { createMockChat } from './mockChat';
import { 
  useHandleSend, 
  useRegenerate, 
  useEdit, 
  useImageSend, 
  useGenerateImage,
  usePersona,
  useRegenerateTracking,
  useEditWithRegenerationHandling
} from '../../hooks/active-chat';


function ActiveChat(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { chatId } = useParams<{ chatId: string }>();
  const searchParams = new URLSearchParams(location.search);
  const playableId = searchParams.get('playable');
  const sourceUrl = location.state?.sourceUrl || '/chats';
  
  const [chatObj, setChatObj] = useState<ChatType | any>(location.state?.chat || null);
  const [message, setMessage] = useState<string>('');
  const [messages, setMessages] = useState<MessageType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showPlayablesPopup, setShowPlayablesPopup] = useState<boolean>(false);
  const [activePlayable, setActivePlayable] = useState<PlayableResult | null>(null);
  const [activeInputable, setActiveInputable] = useState<InputableResult | null>(null);
  const [isInputFocused, setIsInputFocused] = useState<boolean>(false);
  const [isPendingAiResponse, setIsPendingAiResponse] = useState<boolean>(false);
  const [showPersonaBar, setShowPersonaBar] = useState<boolean>(false);
  const [currentMember, setCurrentMember] = useState<ChatMember | null>(null);
  const [hasImageAttachment, setHasImageAttachment] = useState<boolean>(false);
  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<string>('');
  const personaBarRef = useRef<HTMLDivElement>(null);
  const [teaserMessage, setTeaserMessage] = useState<MessageType | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  // Check if we're on mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 480);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Update messages and ensure proper scrolling
  const updateMessages = useCallback((newMessages: MessageType[]) => {
    setMessages(newMessages);
  }, []);

  // Handle showing teaser for AI responses in foreground playables
  const handleGenerateResponse = useCallback((finalChat: ChatType) => {
    if (activePlayable?.behaviorType === 'foreground' && !isInputFocused) {
      const lastMessage = finalChat.messages?.[finalChat.messages.length - 1];
      if (lastMessage?.member_type === 'character') {
        setTeaserMessage(lastMessage);
        setTimeout(() => setTeaserMessage(null), 5000);
      }
    }
  }, [activePlayable, isInputFocused]);

  // Handle preview mode
  const previewCharacter = location.state?.previewCharacter as PreviewCharacter | undefined;
  const isPreview = Boolean(previewCharacter);

  // Add focus listener to refresh chat data when returning to the tab
  useEffect(() => {
    const handleWindowFocus = async () => {
      if (chatObj && chatId && !isPreview) {
        try {
          const chatData = await api.chats.get(chatId);
          setChatObj(chatData);
          updateMessages(chatData.messages || []);
        } catch (err) {
          console.error('Failed to refresh chat on window focus:', err);
        }
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    return () => window.removeEventListener('focus', handleWindowFocus);
  }, [chatObj, chatId, isPreview, updateMessages]);

  // Auto-open playable from URL parameter
  useEffect(() => {
    if (playableId) {
      const playable = getPlayableById(playableId);
      if (playable) {
        setActivePlayable(playable);
      }
    }
  }, [playableId]);

  // Add effect to refresh chat when active playable changes
  useEffect(() => {
    // If we're closing a foreground playable, refresh the chat data
    if (!activePlayable?.behaviorType && chatId && !isPreview) {
      const refreshChatData = async () => {
        try {
          const chatData = await api.chats.get(chatId);
          setChatObj(chatData);
          updateMessages(chatData.messages || []);
        } catch (err) {
          console.error('Failed to refresh chat after closing playable:', err);
        }
      };
      
      refreshChatData();
    }
  }, [activePlayable, chatId, isPreview, updateMessages]);

  useEffect(() => {
    const fetchChatData = async () => {
      try {
        if (isPreview) {
          // For preview mode, fetch character and create preview message
          const character = await api.characters.get(previewCharacter!.username);
          const previewMessage = {
            id: 'preview-msg',
            member_type: 'character',
            icon: character.icon,
            display_name: character.display_name,
            username: character.username,
            content: character.greeting,
            content_type: 'message',
            time_created: new Date().toISOString()
          };
          updateMessages([previewMessage]);
          
          // Create a simplified mock chat object for preview mode
          const mockChat = createMockChat(character);
          
          setChatObj(mockChat);
          setIsLoading(false);
          return;
        }

        const chatData = await api.chats.get(chatId!);
        setChatObj(chatData);
        updateMessages(chatData.messages || []);
        setIsLoading(false);
        chatData.bump().catch(err => console.error('Failed to bump chat:', err));
      } catch (err) {
        console.error('Failed to fetch chat:', err);
        setError('Failed to load chat');
        setIsLoading(false);
      }
    };

    fetchChatData();
  }, [chatId, isPreview, previewCharacter, updateMessages]);

  // Update currentMember whenever chat data changes
  useEffect(() => {
    if (chatObj?.members) {
      const member = chatObj.members.find((m: any) => 
        m.member_type === 'persona' || m.member_type === 'user'
      );
      setCurrentMember(member || { member_type: 'user' });
    }
  }, [chatObj?.members]);

  // Use persona hook
  const { getMessagePersona, handlePersonaSelect } = usePersona({
    chatObj,
    setChatObj,
    updateMessages,
    currentMember
  });

  // Use our other hooks
  const { handleSend } = useHandleSend({
    chatObj,
    messages,
    updateMessages: setMessages,
    setMessage,
    message,
    setIsPendingAiResponse,
    activePlayable,
    currentMember,
    isPreview,
    setShowLoginModal,
    getMessagePersona,
    handleGenerateResponse
  });

  // Use regenerate hook
  const { handleRegenerate, regeneratingMessageId } = useRegenerate({
    chatObj,
    setChatObj,
    updateMessages,
    messagesContainerRef,
    isMobile,
    activePlayable
  });

  const {
    regenerationHistory,
    selectedRegenerationIndices,
    handleRegenerateWithTracking,
    handleSelectRegeneration,
    handleEditRegeneratedMessage,
    completeEdit
  } = useRegenerateTracking({
    chatObj,
    setChatObj,
    messages,
    handleRegenerate
  });

  const { handleEdit: originalHandleEdit } = useEdit({
    chatObj,
    setChatObj,
    messages,
    updateMessages,
    activePlayable,
    setIsPendingAiResponse
  });

  // Use enhanced edit hook that handles regeneration
  const { handleEdit } = useEditWithRegenerationHandling({
    originalHandleEdit,
    regenerationHistory,
    handleEditRegeneratedMessage,
    completeEdit
  });

  const { handleImageSend } = useImageSend({
    chatObj,
    updateMessages,
    setIsPendingAiResponse,
    activePlayable,
    isPreview,
    setShowLoginModal,
    getMessagePersona,
    handleGenerateResponse
  });

  const { handleGenerateImage } = useGenerateImage({
    chatObj,
    setChatObj,
    updateMessages,
    activePlayable,
    getMessagePersona,
    handleGenerateResponse
  });

  const handlePlayableSelect = (playable: PlayableResult | null) => {
    if (playable) {
      // Simplify by directly casting to the expected type
      api.playables.bump(playable.id as any);
      setActivePlayable(playable);
      setActiveInputable(null); // Clear active inputable when selecting a playable
    }
    setShowPlayablesPopup(false);
  };

  const handleInputableSelect = (inputable: InputableResult | null) => {
    if (inputable) {
      // Simplify by directly casting to the expected type
      api.playables.bump(inputable.id as any);
      setActiveInputable(inputable);
      setActivePlayable(null); // Clear active playable when selecting an inputable
    }
    setShowPlayablesPopup(false);
  };

  const handleRewind = async (messageId: string) => {
    try {
      const updatedChat = await chatObj!.rewindToMessage(messageId);
      setChatObj(updatedChat);
      updateMessages(updatedChat.messages || []);
    } catch (err) {
      console.error('Failed to rewind chat:', err);
    }
  };

  const onBack = () => {
    if (showPlayablesPopup) {
      setShowPlayablesPopup(false);
    } else if (activePlayable?.behaviorType === 'foreground') {
      setActivePlayable(null);
    } else {
      navigate(sourceUrl);
    }
  };

  // Add click handler to hide persona bar when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // Don't hide if clicking inside the persona bar
      if (showPersonaBar && 
          personaBarRef.current && 
          !personaBarRef.current.contains(e.target as Node)) {
        setShowPersonaBar(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showPersonaBar]);

  // Compute whether to show chat settings (persona bar trigger)
  const hasOnlyGreetingMessage = messages.length === 1;

  // Handle summaries from playable components
  const handlePlayableSummary = async (summary: string) => {
    if (!chatObj) return;

    try {
      setIsPendingAiResponse(true);
      const updatedChat = await chatObj.addSummary(summary);
      // Simplify by directly casting to the expected type
      const finalChat = await updatedChat.generate(activePlayable?.id as any);
      updateMessages(finalChat.messages || []);
      handleGenerateResponse(finalChat);
    } catch (err) {
      console.error('Failed to handle playable summary:', err);
    } finally {
      setIsPendingAiResponse(false);
    }
  };

  if (error) {
    return <div className={styles.activeChat}>{error}</div>;
  }

  // Create a simplified chat interface object for the messages component
  const chatInterface = {
    newChatFromMessage: async (messageId: string) => {
      try {
        if (!chatObj) throw new Error("Chat object not available");
        setNotificationMessage('New chat created');
        setShowNotification(true);
        setTimeout(() => setShowNotification(false), 3000);
        return await chatObj.newChatFromMessage(messageId);
      } catch (error) {
        console.error("Failed to create new chat from message:", error);
        throw error;
      }
    },
    icon: chatObj?.icon,
    display_name: chatObj?.display_name
  };

  return (
    <div className={styles.activeChat}>
      <ActiveChatTopBar 
        chat={chatObj}
        onBack={onBack}
        activePlayable={activePlayable}
        onStopBackground={() => setActivePlayable(null)}
        showChatSettings={hasOnlyGreetingMessage}
        onChatSettingsClick={() => setShowPersonaBar(true)}
      />

      {showNotification && (
        <div className={styles.notification}>
          {notificationMessage}
        </div>
      )}

      <div className={styles.content}>
        {!isMobile && (
          <PlayablesCatalog 
            onPlayableSelect={handlePlayableSelect}
            onInputableSelect={handleInputableSelect}
            setShowPlayablesPopup={setShowPlayablesPopup}
            isVisible={showPlayablesPopup}
            characterDisplayName={chatObj?.display_name || 'your character'}
          />
        )}
        {(activePlayable?.behaviorType === 'foreground') ? (
          <>
            <activePlayable.component 
              onPlayableSummary={handlePlayableSummary}
              onBack={onBack}
              showPlayablesPopup={showPlayablesPopup}
              setShowPlayablesPopup={setShowPlayablesPopup}
              isPreview={isPreview}
              setShowLoginModal={setShowLoginModal}
              onInputableSelect={handleInputableSelect}
            />
            {/* Don't show message overlay for YouTube */}
            {isInputFocused && activePlayable?.id !== 'youtube' && (
              <ActiveChatMessages 
                messages={messages} 
                chat={chatInterface}
                showPlayablesPopup={showPlayablesPopup}
                isOverlay={true}
                onRegenerate={handleRegenerateWithTracking}
                onRewind={handleRewind}
                onEdit={handleEdit}
                regeneratingMessageId={regeneratingMessageId}
                isPendingAiResponse={isPendingAiResponse}
                onAvatarClick={() => setShowPersonaBar(true)}
                hideRegenerateButton={showPersonaBar || hasImageAttachment}
                isMobile={isMobile}
                ref={messagesContainerRef}
                regenerationHistory={regenerationHistory}
                selectedRegenerationIndices={selectedRegenerationIndices}
                onSelectRegeneration={handleSelectRegeneration}
              />
            )}
            {teaserMessage && !isInputFocused && activePlayable?.id !== 'youtube' && (
              <div className={styles.teaserContainer}>
                <TeaserMessage 
                  message={teaserMessage}
                  onDismiss={() => setTeaserMessage(null)}
                />
              </div>
            )}
          </>
        ) : isLoading ? (
          <div className={styles.messageList}>Loading...</div>
        ) : (
          <ActiveChatMessages 
            messages={messages} 
            chat={chatInterface}
            showPlayablesPopup={showPlayablesPopup}
            onRegenerate={handleRegenerateWithTracking}
            onRewind={handleRewind}
            onEdit={handleEdit}
            regeneratingMessageId={regeneratingMessageId}
            isPendingAiResponse={isPendingAiResponse}
            onAvatarClick={() => setShowPersonaBar(true)}
            hideRegenerateButton={showPersonaBar || hasImageAttachment}
            isMobile={isMobile}
            ref={messagesContainerRef}
            regenerationHistory={regenerationHistory}
            selectedRegenerationIndices={selectedRegenerationIndices}
            onSelectRegeneration={handleSelectRegeneration}
          />
        )}
      </div>

      {/* Mobile Drawer for Playables Catalog */}
      {isMobile && (
        <PlayablesCatalogMobileDrawer
          onPlayableSelect={handlePlayableSelect}
          onInputableSelect={handleInputableSelect}
          setShowPlayablesPopup={setShowPlayablesPopup}
          isVisible={showPlayablesPopup}
          characterDisplayName={chatObj?.display_name || 'your character'}
        />
      )}

      {showPersonaBar ? (
        <ActiveChatPersonaBar 
          containerRef={personaBarRef}
          onPersonaSelect={handlePersonaSelect}
          chat={chatObj}
        />
      ) : (
        // Hide the input bar when YouTube is the active playable
        !(activePlayable?.id === 'youtube') && (
          <ActiveChatInputBar
            message={message}
            setMessage={setMessage}
            handleSend={handleSend}
            handleImageSend={handleImageSend}
            handleGenerateImage={handleGenerateImage}
            showPlayablesPopup={showPlayablesPopup}
            setShowPlayablesPopup={setShowPlayablesPopup}
            chat={chatObj}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => setIsInputFocused(false)}
            isPreview={isPreview}
            // Simplify by using type assertions for these props
            activePlayable={activePlayable as any}
            activeInputable={activeInputable as any}
            onImageAttachmentChange={setHasImageAttachment}
            setShowLoginModal={setShowLoginModal}
          />
        )
      )}
      
      {showLoginModal && (
        <LoginModal 
          redirectParams={{
            redirectType: 'chat',
            characterUsername: previewCharacter?.username || ''
          }}
          onClose={() => setShowLoginModal(false)}
        />
      )}
    </div>
  );
}

export default ActiveChat;